import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  IconButton,
  Avatar,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Divider,
  Grid,
  Menu,
  MenuItem,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material"
import LogoutIcon from "@mui/icons-material/Logout"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import RefreshIcon from "@mui/icons-material/Refresh"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import UndoIcon from "@mui/icons-material/Undo"
import PaymentIcon from "@mui/icons-material/Payment"
import InventoryIcon from "@mui/icons-material/Inventory"
import MapIcon from "@mui/icons-material/Map"
import DoneAllIcon from "@mui/icons-material/DoneAll"
import PersonIcon from "@mui/icons-material/Person"
import PlaceIcon from "@mui/icons-material/Place"
import LabelIcon from "@mui/icons-material/Label"
import LanguageIcon from "@mui/icons-material/Language"
import CheckIcon from "@mui/icons-material/Check"

// Language options for driver page
const DRIVER_LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "gr", label: "Ελληνικά", flag: "🇬🇷" },
  { code: "al", label: "Shqip", flag: "🇦🇱" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "es", label: "Español", flag: "🇪🇸" },
]

import { getShipmentsDriver, getOrdersbyid, getAppUserById } from "helpers/api_helper"
import { getLoans, getCustomers } from "helpers/api_helper"
import { transformLoansData } from "helpers/generalfunctions"
import { patchOrder, patchShipment } from "helpers/api_helper"

import OptimalRouteMap from "pages/Orders/optimalroute"
import LoanModalCust from "pages/loans/loanreturncust"
import LoanModal from "pages/loans/loanreturnmodal"
import PaymentModal from "./paymentmodal"

const MuiDriverPage = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { driverId } = useParams()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")) // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")) // 600-900px
  const isDesktop = useMediaQuery(theme.breakpoints.up("md")) // > 900px

  const [shipments, setShipments] = useState([])
  const [loans, setLoans] = useState([])
  const [selectedShipment, setSelectedShipment] = useState(null)
  const [selectedOrders, setSelectedOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [driverData, setDriverData] = useState(null)
  const [driverName, setDriverName] = useState("")
  const [ordersLoading, setOrdersLoading] = useState(false)
  const [showMap, setShowMap] = useState(true)
  const [addresses, setAddresses] = useState([])
  const [routeDetails, setRouteDetails] = useState([])
  const [returnsModal, setReturnsModal] = useState(false)
  const [returnOrder, setReturnOrder] = useState(null)
  const [customerss, setCustomerss] = useState([])
  const [customers, setCustomers] = useState([])

  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState(null)

  // Language menu state
  const [langAnchorEl, setLangAnchorEl] = useState(null)
  const [currentLang, setCurrentLang] = useState(() => {
    // Get saved driver language or default to English
    return localStorage.getItem("driverLanguage") || "en"
  })

  // Set language on mount
  useEffect(() => {
    const savedLang = localStorage.getItem("driverLanguage") || "en"
    i18n.changeLanguage(savedLang)
    setCurrentLang(savedLang)
  }, [])

  const handleLangMenuOpen = (event) => {
    setLangAnchorEl(event.currentTarget)
  }

  const handleLangMenuClose = () => {
    setLangAnchorEl(null)
  }

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode)
    setCurrentLang(langCode)
    localStorage.setItem("driverLanguage", langCode)
    handleLangMenuClose()
  }

  const getCurrentLanguageLabel = () => {
    const lang = DRIVER_LANGUAGES.find((l) => l.code === currentLang)
    return lang ? lang.flag : "🌐"
  }

  const openPaymentModal = (customerId, customerName) => {
    setSelectedCustomer({ id: customerId, name: customerName })
    setIsPaymentModalOpen(true)
  }

  useEffect(() => {
    // Fetch driver details by FlexFlow user ID from URL parameter
    if (driverId) {
      fetchDriverDetails(driverId)
    }
  }, [driverId])

  const fetchDriverDetails = async (dfUserId) => {
    try {
      // Get user directly from FlexFlow
      const user = await getAppUserById(dfUserId)
      if (user) {
        setDriverData(user)
        const fullName = user.last_name
          ? `${user.first_name || ""} ${user.last_name}`.trim()
          : user.first_name || user.name || user.email
        setDriverName(fullName)
        // Fetch shipments using the FlexFlow user ID directly
        fetchShipments(dfUserId)
      } else {
        console.error("User not found for DF user ID:", dfUserId)
        setLoading(false)
      }
    } catch (error) {
      console.error("Error fetching user:", error)
      setLoading(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem("authUser")
    navigate("/login")
  }

  const fetchLoansData = () => {
    getLoans().then((res) => {
      setLoans(res)
      setCustomerss(transformLoansData(res))
      getCustomers().then((res) => setCustomers(res))
    })
  }

  const fetchShipments = async (driverIdParam) => {
    try {
      // Pass driver_id to fetch shipments for this driver
      const data = await getShipmentsDriver(driverIdParam)
      setShipments(data || [])
    } catch (error) {
      console.error("Error fetching shipments:", error)
      setShipments([])
    } finally {
      setLoading(false)
      fetchLoansData()
    }
  }

  const handleSelectShipment = async (shipment) => {
    setSelectedShipment(shipment)
    setAddresses(JSON.parse(shipment.route))
    setSelectedOrders([])
    setOrdersLoading(true)

    try {
      const orderIds = JSON.parse(shipment.orders).map((order) => order.id)
      const orderDetails = await Promise.all(orderIds.map((id) => getOrdersbyid(id)))
      const filteredOrders = orderDetails.flat().filter((order) => order.status === "ON SHIPPING")
      setSelectedOrders(filteredOrders)
    } catch (error) {
      console.error("Error fetching orders:", error)
    } finally {
      setOrdersLoading(false)
    }
  }

  const finalizeShipment = async () => {
    if (selectedShipment && selectedOrders.length === 0) {
      try {
        const payload1 = { resource: [{ id: selectedShipment.id, status: "DONE" }] }
        await patchShipment(payload1)
        fetchShipments(driverData?.id)
      } catch (error) {
        console.error("Error updating shipment status:", error)
      }
    } else {
      alert(t("Shipment cannot be finalized, orders still exist."))
    }
  }

  const updateOrderStatus = async (orderId, status) => {
    try {
      const payload = {
        resource: [{ id: orderId, status: status }],
      }
      const res = await patchOrder(payload)
      if (res && res.resource[0]) {
        setSelectedOrders((prevOrders) => prevOrders.filter((order) => order.id !== orderId))
      }
    } catch (error) {
      console.error("Error updating order status:", error)
    }
  }

  const openReturnsModal = (order) => {
    setReturnOrder(order)
    setReturnsModal(true)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "SHIPPED":
        return "success"
      case "INVOICED":
        return "warning"
      case "RETURNED":
        return "error"
      default:
        return "default"
    }
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", p: { xs: 1, sm: 1.5, md: 2 } }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 1.5, sm: 2 },
          borderRadius: 2,
          border: "1px solid #e2e8f0",
          background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          spacing={{ xs: 1.5, sm: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }}>
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }}>
              <LocalShippingIcon sx={{ color: "#fff", fontSize: { xs: 20, sm: 24 } }} />
            </Avatar>
            <Box>
              <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={600} color="#fff">
                {t("Driver Panel")}
              </Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.8)" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                {driverName}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} justifyContent={{ xs: "flex-end", sm: "flex-start" }}>
            {/* Language Selector */}
            <Tooltip title={t("Language")}>
              <IconButton
                onClick={handleLangMenuOpen}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                  color: "#fff",
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                }}
              >
                <Typography fontSize={{ xs: "1rem", sm: "1.2rem" }}>{getCurrentLanguageLabel()}</Typography>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={langAnchorEl}
              open={Boolean(langAnchorEl)}
              onClose={handleLangMenuClose}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                  minWidth: 160,
                },
              }}
            >
              {DRIVER_LANGUAGES.map((lang) => (
                <MenuItem
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  selected={currentLang === lang.code}
                  sx={{
                    py: 1,
                    "&.Mui-selected": {
                      bgcolor: "rgba(59, 130, 246, 0.08)",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Typography fontSize="1.2rem">{lang.flag}</Typography>
                  </ListItemIcon>
                  <ListItemText
                    primary={lang.label}
                    primaryTypographyProps={{
                      fontSize: "0.875rem",
                      fontWeight: currentLang === lang.code ? 600 : 400,
                    }}
                  />
                  {currentLang === lang.code && (
                    <CheckIcon sx={{ fontSize: 18, color: "#3b82f6", ml: 1 }} />
                  )}
                </MenuItem>
              ))}
            </Menu>

            <Tooltip title={t("Returns")}>
              <IconButton
                onClick={() => openReturnsModal({})}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                  color: "#fff",
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                }}
              >
                <InventoryIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("Logout")}>
              <IconButton
                onClick={handleLogout}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                  color: "#fff",
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                }}
              >
                <LogoutIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {/* Shipments List */}
      <Paper elevation={0} sx={{ mb: { xs: 1.5, sm: 2 }, borderRadius: 2, border: "1px solid #e2e8f0" }}>
        <Box sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 1.5 }, borderBottom: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" fontWeight={600} color="#475569" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
              {t("Active Routes")}
            </Typography>
            <IconButton size="small" onClick={() => fetchShipments(driverData?.id)}>
              <RefreshIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
            </IconButton>
          </Stack>
        </Box>

        {loading ? (
          <Box sx={{ p: { xs: 3, sm: 4 }, textAlign: "center" }}>
            <CircularProgress size={isMobile ? 28 : 32} />
          </Box>
        ) : shipments.length === 0 ? (
          <Box sx={{ p: { xs: 3, sm: 4 }, textAlign: "center" }}>
            <Typography color="#64748b" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>{t("No active shipments")}</Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ maxHeight: { xs: 200, sm: 250 }, overflow: "auto" }}>
            {shipments.map((shipment, index) => (
              <React.Fragment key={shipment.id}>
                {index > 0 && <Divider />}
                <ListItemButton
                  selected={selectedShipment?.id === shipment.id}
                  onClick={() => handleSelectShipment(shipment)}
                  sx={{
                    py: { xs: 1, sm: 1.5 },
                    px: { xs: 1.5, sm: 2 },
                    "&.Mui-selected": {
                      bgcolor: "rgba(59, 130, 246, 0.08)",
                      borderLeft: "3px solid #3b82f6",
                    },
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                    gap: { xs: 1, sm: 0 },
                  }}
                >
                  <ListItemText
                    sx={{ m: 0 }}
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                          {t("Route")} #{shipment.id}
                        </Typography>
                        <Chip
                          label={shipment.status || "ACTIVE"}
                          size="small"
                          color={shipment.status === "DONE" ? "success" : "primary"}
                          sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                        />
                      </Stack>
                    }
                    secondary={
                      <Typography variant="caption" color="#64748b" sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                        {t("Updated")}: {shipment.update}
                      </Typography>
                    }
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation()
                      finalizeShipment()
                    }}
                    startIcon={<DoneAllIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                    sx={{
                      ml: { xs: 0, sm: 1 },
                      alignSelf: { xs: "flex-end", sm: "center" },
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      py: { xs: 0.5, sm: 0.75 },
                      minWidth: { xs: "auto", sm: 80 },
                    }}
                  >
                    {t("Finish")}
                  </Button>
                </ListItemButton>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Orders Section */}
      {ordersLoading ? (
        <Box sx={{ p: { xs: 3, sm: 4 }, textAlign: "center" }}>
          <CircularProgress size={isMobile ? 28 : 32} />
        </Box>
      ) : (
        selectedOrders.length > 0 && (
          <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}>
            {/* Map Toggle */}
            <Box
              sx={{
                px: { xs: 1.5, sm: 2 },
                py: { xs: 1, sm: 1.5 },
                borderBottom: "1px solid #e2e8f0",
                bgcolor: "#f8fafc",
                cursor: "pointer",
              }}
              onClick={() => setShowMap((prev) => !prev)}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <MapIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: "#64748b" }} />
                  <Typography variant="subtitle2" fontWeight={600} color="#475569" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                    {t("Route Map")}
                  </Typography>
                </Stack>
                {showMap ? <ExpandLessIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /> : <ExpandMoreIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />}
              </Stack>
            </Box>
            <Collapse in={showMap}>
              {addresses.length > 0 && (
                <Box sx={{ height: { xs: 200, sm: 250, md: 300 } }}>
                  <OptimalRouteMap
                    Dontshow={!showMap}
                    addresses={addresses}
                    onRouteCalculated={setRouteDetails}
                  />
                </Box>
              )}
            </Collapse>

            {/* Orders Header */}
            <Box sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 1.5 }, borderBottom: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" fontWeight={600} color="#475569" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                  {t("Orders")} ({selectedOrders.length})
                </Typography>
              </Stack>
            </Box>

            {/* Orders List */}
            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Stack spacing={{ xs: 1.5, sm: 2 }}>
                {selectedOrders.map((order) => {
                  const customerLoans = customerss.find((c) => c.customerid === order.customer_id)
                  const totalLoans = customerLoans
                    ? customerLoans.custloans.reduce((sum, loan) => sum + loan.total, 0)
                    : 0
                  const isInvoiced = order.invoicedAt !== "epsilon"
                  const totals = JSON.parse(order.totals || "{}")
                  const deliveryAddress = JSON.parse(order.delivery_address || "{}")

                  return (
                    <Paper
                      key={order.id}
                      elevation={0}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: isInvoiced ? "#fca5a5" : "#86efac",
                        bgcolor: isInvoiced ? "#fef2f2" : "#f0fdf4",
                      }}
                    >
                      {/* Order Info */}
                      <Stack spacing={{ xs: 1, sm: 1.5 }}>
                        <Stack direction="row" alignItems="flex-start" spacing={1}>
                          <PlaceIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: "#64748b", mt: 0.3 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={500} color="#1e293b" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, wordBreak: "break-word" }}>
                              {deliveryAddress.address}
                            </Typography>
                          </Box>
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={1}>
                          <PersonIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: "#64748b" }} />
                          <Typography variant="body2" color="#475569" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                            {isMobile && order.name?.length > 25 ? `${order.name.substring(0, 25)}...` : order.name?.length > 40 ? `${order.name.substring(0, 40)}...` : order.name}
                          </Typography>
                        </Stack>

                        {order.label?.length > 2 && (
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <LabelIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: "#64748b" }} />
                            <Typography variant="body2" color="#475569" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                              {order.label}
                            </Typography>
                          </Stack>
                        )}

                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          alignItems={{ xs: "flex-start", sm: "center" }}
                          spacing={{ xs: 0.5, sm: 2 }}
                          flexWrap="wrap"
                        >
                          <Chip
                            label={order.status}
                            size="small"
                            color={getStatusColor(order.status)}
                            sx={{ height: { xs: 20, sm: 22 }, fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                          />
                          <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }}>
                            <Typography variant="body2" color="#64748b" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                              {t("Products")}: <strong>{totals.totalItems || 0}</strong>
                            </Typography>
                            <Typography variant="body2" color="#64748b" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                              {t("Total")}: <strong>€{Number(totals.totalOrder || 0).toFixed(2)}</strong>
                            </Typography>
                          </Stack>
                        </Stack>

                        {/* Action Buttons */}
                        <Grid container spacing={{ xs: 0.75, sm: 1 }} sx={{ mt: { xs: 0.5, sm: 1 } }}>
                          <Grid item xs={6} sm={6} md="auto">
                            <Button
                              fullWidth
                              variant="contained"
                              size="small"
                              color="primary"
                              onClick={() => updateOrderStatus(order.id, "SHIPPED")}
                              startIcon={<CheckCircleIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                              sx={{
                                textTransform: "none",
                                fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                py: { xs: 0.75, sm: 1 },
                                px: { xs: 1, sm: 1.5 },
                              }}
                            >
                              {t("Delivered")}
                            </Button>
                          </Grid>
                          <Grid item xs={6} sm={6} md="auto">
                            <Button
                              fullWidth
                              variant="outlined"
                              size="small"
                              onClick={() => updateOrderStatus(order.id, "INVOICED")}
                              sx={{
                                textTransform: "none",
                                fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                py: { xs: 0.75, sm: 1 },
                                px: { xs: 1, sm: 1.5 },
                              }}
                            >
                              {t("To Planning")}
                            </Button>
                          </Grid>
                          {totalLoans > 0 && (
                            <Grid item xs={6} sm={6} md="auto">
                              <Button
                                fullWidth
                                variant="contained"
                                size="small"
                                color={totalLoans > 10 ? "error" : "warning"}
                                onClick={() => openReturnsModal(order)}
                                startIcon={<InventoryIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                                sx={{
                                  textTransform: "none",
                                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                  py: { xs: 0.75, sm: 1 },
                                  px: { xs: 1, sm: 1.5 },
                                }}
                              >
                                {t("Loans")} ({totalLoans})
                              </Button>
                            </Grid>
                          )}
                          <Grid item xs={6} sm={6} md="auto">
                            <Button
                              fullWidth
                              variant="contained"
                              size="small"
                              color="success"
                              onClick={() => openPaymentModal(order.customer_id, order.name)}
                              startIcon={<PaymentIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                              sx={{
                                textTransform: "none",
                                fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                py: { xs: 0.75, sm: 1 },
                                px: { xs: 1, sm: 1.5 },
                              }}
                            >
                              {t("Payments")}
                            </Button>
                          </Grid>
                          <Grid item xs={6} sm={6} md="auto">
                            <Button
                              fullWidth
                              variant="contained"
                              size="small"
                              color="error"
                              onClick={() => updateOrderStatus(order.id, "RETURNED")}
                              startIcon={<UndoIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                              sx={{
                                textTransform: "none",
                                fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                py: { xs: 0.75, sm: 1 },
                                px: { xs: 1, sm: 1.5 },
                              }}
                            >
                              {t("Return")}
                            </Button>
                          </Grid>
                        </Grid>
                      </Stack>
                    </Paper>
                  )
                })}
              </Stack>
            </Box>
          </Paper>
        )
      )}

      {/* Modals */}
      {returnOrder && returnOrder.customer_id ? (
        <LoanModalCust
          fetchLoansData={fetchLoansData}
          loans={loans}
          customers={customers}
          setLoanModal={setReturnsModal}
          isOpen={returnsModal}
          order={returnOrder}
        />
      ) : (
        <LoanModal
          fetchLoansData={fetchLoansData}
          loans={loans}
          customers={customers}
          setLoanModal={setReturnsModal}
          isOpen={returnsModal}
        />
      )}

      {selectedCustomer && (
        <PaymentModal
          isOpen={isPaymentModalOpen}
          toggle={() => setIsPaymentModalOpen(false)}
          customerId={selectedCustomer.id}
          customerName={selectedCustomer.name}
        />
      )}
    </Box>
  )
}

export default MuiDriverPage
